// MediaCropper — recortador interactivo de imágenes con 3 aspect ratios y 2 calidades.
//
// Funcionalidad:
//  - Pan via drag (pointer events)
//  - Zoom via slider (1x .. 3x)
//  - Selector aspect ratio (16:9 / 1:1 / 9:16)
//  - Selector de calidad (FHD 1080p / 4K 2160p)
//  - Apply → devuelve dataURL recortado al tamaño exacto vía canvas
//
// Para video, MediaCropper NO re-encodea (browser puro no puede sin ffmpeg.wasm).
// Solo deja elegir aspect ratio y lo guarda como metadato `cropAspect` para
// que los players muestren con object-fit:cover en ese aspect.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Square, RectangleHorizontal, RectangleVertical, ZoomIn, Check, X, Sparkles } from 'lucide-react';
import { tr } from '@/contexts/LanguageContext';

export const ASPECT_RATIOS = [
  { id: '16:9', label: '16:9 — Horizontal', ratio: 16 / 9, icon: RectangleHorizontal },
  { id: '1:1',  label: '1:1 — Cuadrado',     ratio: 1,        icon: Square },
  { id: '9:16', label: '9:16 — Vertical',    ratio: 9 / 16,   icon: RectangleVertical }
];

// width × height destino por aspect+quality.
export const QUALITY_PRESETS = {
  fhd: {
    id: 'fhd', label: 'FHD · 1080p', short: 'FHD',
    dims: { '16:9': [1920, 1080], '1:1': [1080, 1080], '9:16': [1080, 1920] }
  },
  '4k': {
    id: '4k', label: '4K · 2160p', short: '4K',
    dims: { '16:9': [3840, 2160], '1:1': [2160, 2160], '9:16': [2160, 3840] }
  },
  '8k': {
    id: '8k', label: '8K · 4320p', short: '8K',
    dims: { '16:9': [7680, 4320], '1:1': [4320, 4320], '9:16': [4320, 7680] }
  }
};

// Set de calidades de VIDEO (metadato — no se re-encodea en browser).
// El backend, al subir, transcodifica al preset elegido por el usuario.
export const VIDEO_QUALITIES = {
  sd:  { id: 'sd',  label: 'SD · 480p',   short: 'SD',  height: 480 },
  hd:  { id: 'hd',  label: 'HD · 720p',   short: 'HD',  height: 720 },
  fhd: { id: 'fhd', label: 'FHD · 1080p', short: 'FHD', height: 1080 },
  qhd: { id: 'qhd', label: 'QHD · 1440p', short: '2K',  height: 1440 },
  '4k':{ id: '4k',  label: '4K · 2160p',  short: '4K',  height: 2160 }
};

const PREVIEW_W = 320; // ancho preview dentro del modal (responsive en CSS)

export const MediaCropper = ({
  open,
  onOpenChange,
  src,                         // dataUrl de la imagen original
  defaultAspect = '1:1',
  defaultQuality = 'fhd',
  qualityOptions = ['fhd', '4k'],   // ids visibles en el selector
  lockQuality = false,              // si true, oculta el selector de calidad
  onApply                      // ({ dataUrl, aspect, quality, width, height }) => void
}) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [imgNat, setImgNat] = useState({ w: 0, h: 0 });
  const [aspect, setAspect] = useState(defaultAspect);
  const [quality, setQuality] = useState(defaultQuality);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [containerW, setContainerW] = useState(PREVIEW_W);
  const dragRef = useRef(null);

  const aspectRatio = ASPECT_RATIOS.find((a) => a.id === aspect)?.ratio || 1;

  // Frame dimensions derived from container width and aspect
  const frame = useMemo(() => {
    const fw = containerW;
    const fh = fw / aspectRatio;
    return { w: fw, h: fh };
  }, [containerW, aspectRatio]);

  // Base scale = cover (image siempre llena el frame mínimo)
  const baseScale = useMemo(() => {
    if (!imgNat.w || !imgNat.h) return 1;
    return Math.max(frame.w / imgNat.w, frame.h / imgNat.h);
  }, [imgNat, frame]);

  const scale = baseScale * zoom;
  const IW = imgNat.w * scale;
  const IH = imgNat.h * scale;

  // Bound offset so image always covers frame
  const clampOffset = useCallback((next) => {
    const maxX = Math.max(0, (IW - frame.w) / 2);
    const maxY = Math.max(0, (IH - frame.h) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, next.x)),
      y: Math.max(-maxY, Math.min(maxY, next.y))
    };
  }, [IW, IH, frame]);

  // Reset offset cuando cambia aspect / zoom
  useEffect(() => {
    setOffset((o) => clampOffset(o));
  }, [clampOffset]);

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setAspect(defaultAspect);
      setQuality(defaultQuality);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    }
  }, [open, defaultAspect, defaultQuality]);

  // Detectar ancho del contenedor (responsive)
  useEffect(() => {
    if (!containerRef.current || !open) return;
    const ro = new ResizeObserver(() => {
      setContainerW(containerRef.current?.clientWidth || PREVIEW_W);
    });
    ro.observe(containerRef.current);
    setContainerW(containerRef.current.clientWidth || PREVIEW_W);
    return () => ro.disconnect();
  }, [open]);

  // Cargar dimensiones naturales de la imagen
  const handleImgLoad = (e) => {
    setImgNat({ w: e.target.naturalWidth, h: e.target.naturalHeight });
    setOffset({ x: 0, y: 0 });
  };

  // Drag handlers
  const onPointerDown = (e) => {
    e.preventDefault();
    e.target.setPointerCapture?.(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: offset.x, baseY: offset.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(clampOffset({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy }));
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    e.target.releasePointerCapture?.(e.pointerId);
  };

  // Apply → canvas crop
  const handleApply = () => {
    const [outW, outH] = QUALITY_PRESETS[quality].dims[aspect];
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');

    // source rect in natural image coords
    const sw = frame.w / scale;
    const sh = frame.h / scale;
    const sx = imgNat.w / 2 - (frame.w / 2 + offset.x) / scale;
    const sy = imgNat.h / 2 - (frame.h / 2 + offset.y) / scale;

    const img = imgRef.current;
    if (!img) return;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
    const out = canvas.toDataURL('image/jpeg', 0.92);
    onApply?.({
      dataUrl: out,
      aspect,
      quality,
      width: outW,
      height: outH
    });
    onOpenChange?.(false);
  };

  // ============================ RENDER ============================
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-card border-border p-0 overflow-hidden max-w-3xl w-[calc(100vw-1rem)] max-h-[95dvh] flex flex-col gap-0"
        data-testid="media-cropper"
      >
        {/* HEADER · pinned · iter 92: layout estructural fijo */}
        <header className="flex-shrink-0 px-4 pt-4 pb-3 border-b border-border">
          <h2 className="font-display flex items-center gap-2 text-base font-bold text-foreground">
            <Sparkles className="w-5 h-5 text-primary" />{tr("Recortar y elegir calidad")}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{tr("Arrastra la imagen para reposicionar, ajusta zoom y selecciona aspect ratio.")}</p>
        </header>

        {/* BODY · flex-1 scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4">

        {/* PREVIEW */}
        <div
          ref={containerRef}
          className="relative bg-black rounded-xl overflow-hidden mx-auto select-none"
          style={{ width: '100%', maxWidth: 480, height: frame.h, touchAction: 'none' }}
          data-testid="cropper-preview"
        >
          {/* Image draggable */}
          <img
            ref={imgRef}
            src={src}
            alt="cropper"
            onLoad={handleImgLoad}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            className="absolute pointer-events-auto cursor-grab active:cursor-grabbing"
            style={{
              width: IW,
              height: IH,
              left: '50%',
              top: '50%',
              transform: `translate(${offset.x - IW / 2}px, ${offset.y - IH / 2}px)`
            }}
            draggable={false}
            data-testid="cropper-image"
          />
          {/* Frame overlay (just an inner border) */}
          <div className="absolute inset-0 pointer-events-none border border-primary/60" />
          {/* Aspect badge */}
          <div className="absolute top-2 left-2 bg-background/80 backdrop-blur rounded-md px-2 py-0.5 text-[10px] text-foreground font-mono pointer-events-none">
            {aspect} · {QUALITY_PRESETS[quality].dims[aspect][0]}×{QUALITY_PRESETS[quality].dims[aspect][1]}
          </div>
        </div>

        {/* ASPECT RATIO BUTTONS */}
        <div>
          <p className="text-xs text-muted-foreground mb-2 font-semibold uppercase tracking-widest">Aspect ratio</p>
          <div className="grid grid-cols-3 gap-2" data-testid="cropper-aspect-row">
            {ASPECT_RATIOS.map((ar) => {
              const Icon = ar.icon;
              const active = ar.id === aspect;
              return (
                <button
                  key={ar.id}
                  type="button"
                  onClick={() => setAspect(ar.id)}
                  data-testid={`cropper-aspect-${ar.id.replace(':', '-')}`}
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                    active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card text-foreground hover:border-primary/40'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[11px] font-semibold">{ar.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* QUALITY BUTTONS */}
        {!lockQuality && (
        <div>
          <p className="text-xs text-muted-foreground mb-2 font-semibold uppercase tracking-widest">{tr("Calidad de salida")}</p>
          <div className={`grid gap-2 grid-cols-${qualityOptions.length}`} data-testid="cropper-quality-row">
            {qualityOptions.map((qid) => {
              const q = QUALITY_PRESETS[qid];
              if (!q) return null;
              const active = q.id === quality;
              const [w, h] = q.dims[aspect];
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => setQuality(q.id)}
                  data-testid={`cropper-quality-${q.id}`}
                  className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                    active
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-card hover:border-primary/40'
                  }`}
                >
                  <div className="text-left">
                    <p className={`font-display font-bold text-sm ${active ? 'text-primary' : 'text-foreground'}`}>{q.label}</p>
                    <p className="text-[10px] text-muted-foreground tabular-nums">{w}×{h} px</p>
                  </div>
                  {active && <Check className="w-4 h-4 text-primary" />}
                </button>
              );
            })}
          </div>
        </div>
        )}
        {lockQuality && (
          <div className="bg-primary/10 border border-primary/40 rounded-lg p-2.5 flex items-center justify-between" data-testid="cropper-quality-locked">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              <p className="text-xs text-foreground font-semibold">{tr("Calidad fija para este contexto:")}{' '}<span className="text-primary">{QUALITY_PRESETS[quality]?.label}</span></p>
            </div>
            <span className="text-[10px] text-muted-foreground tabular-nums">{QUALITY_PRESETS[quality]?.dims[aspect].join('×')} px</span>
          </div>
        )}

        {/* ZOOM SLIDER */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-muted-foreground font-semibold uppercase tracking-widest flex items-center gap-1">
              <ZoomIn className="w-3 h-3" /> Zoom
            </p>
            <span className="text-xs text-foreground tabular-nums" data-testid="cropper-zoom-label">{zoom.toFixed(2)}x</span>
          </div>
          <Slider
            value={[zoom]}
            onValueChange={(v) => setZoom(v[0])}
            min={1}
            max={3}
            step={0.05}
            data-testid="cropper-zoom"
          />
          <p className="text-[10px] text-muted-foreground mt-1">{tr("Arrastra la imagen para reposicionar.")}</p>
        </div>

        </div>

        {/* FOOTER · always visible · safe-area inset · iter 92 */}
        <footer
          className="flex-shrink-0 border-t border-border bg-card px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-2"
          data-testid="cropper-footer"
        >
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            className="flex-1 h-11 gap-1"
            data-testid="cropper-cancel"
          >
            <X className="w-4 h-4" />{' '}{tr("Cancelar")}</Button>
          <Button
            onClick={handleApply}
            disabled={!imgNat.w}
            className="flex-1 h-11 bg-primary text-primary-foreground gap-1 shadow-gold disabled:opacity-60"
            data-testid="cropper-apply"
          >
            <Check className="w-4 h-4" />{' '}{tr("Aplicar recorte")}</Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
};
